/**
 * LegacyUiCommandRouter
 *
 * Purpose for this stage:
 *
 *   $rjq normalized operation
 *     -> human-readable legacy UI command
 *
 * This is not an executor yet. It does not call treeStore and it does not
 * change behavior. It gives names to old jQuery-shaped UI mutations so we can
 * discuss and review meaning before converting anything.
 *
 * Examples:
 *
 *   addClass('mcss-ic_fav-cssm') on mcss-fav-cssm_123
 *     -> markSubFavoriteOn
 *
 *   toggleClass('mcss-current_page-cssm', true) on a paging button
 *     -> markPagingButtonCurrent
 *
 *   prop('checked', true) on an input
 *     -> checkInput
 */

export type LegacyUiOpName =
  | 'addClass'
  | 'removeClass'
  | 'toggleClass'
  | 'show'
  | 'hide'
  | 'toggle'
  | 'fadeTo'
  | 'fadeIn'
  | 'fadeOut'
  | 'slideToggle'
  | 'attr'
  | 'removeAttr'
  | 'prop'
  | 'css'
  | 'text'
  | 'html'
  | 'val';

export type LegacyTargetKind =
  | 'sub root'
  | 'sub icon'
  | 'favorite icon'
  | 'quick reply icon'
  | 'sub link'
  | 'sub text'
  | 'sub time'
  | 'sub info'
  | 'subs list'
  | 'paging button'
  | 'paging bar'
  | 'menu item'
  | 'main message body'
  | 'main page chrome'
  | 'new editor form'
  | 'new editor input'
  | 'new editor status'
  | 'recipient chip'
  | 'attachment gallery'
  | 'alert dialog'
  | 'gallery'
  | 'document body'
  | 'legacy data container'
  | 'document'
  | 'window'
  | 'unknown target';

export type LegacyCommandConfidence = 'known' | 'probable' | 'raw';

export interface LegacyUiTarget {
  htmlId?: string | null;
  tagName?: string | null;
  type?: string | null;
  name?: string | null;
  classes?: string[];
  attrs?: Record<string, string | undefined>;
  targetKind?: LegacyTargetKind;
}

export type LegacyClassMode = 'add' | 'remove' | 'toggle';

export type LegacyUiNormalizedOp =
  | {
      op: 'class';
      legacyOp: 'addClass' | 'removeClass' | 'toggleClass';
      target: LegacyUiTarget;
      className: string;
      mode: LegacyClassMode;
      state?: boolean;
      source?: string;
    }
  | {
      op: 'visibility';
      legacyOp: 'show' | 'hide' | 'toggle' | 'fadeIn' | 'fadeOut' | 'slideToggle';
      target: LegacyUiTarget;
      value?: boolean;
      source?: string;
    }
  | {
      op: 'opacity';
      legacyOp: 'fadeTo';
      target: LegacyUiTarget;
      opacity: number;
      source?: string;
    }
  | {
      op: 'attr';
      legacyOp: 'attr' | 'removeAttr';
      target: LegacyUiTarget;
      name: string;
      value?: string | boolean | number | null;
      remove?: boolean;
      source?: string;
    }
  | {
      op: 'prop';
      legacyOp: 'prop';
      target: LegacyUiTarget;
      name: string;
      value: unknown;
      source?: string;
    }
  | {
      op: 'style';
      legacyOp: 'css';
      target: LegacyUiTarget;
      prop: string;
      value: string | number | null | undefined;
      source?: string;
    }
  | {
      op: 'text';
      legacyOp: 'text';
      target: LegacyUiTarget;
      value: string;
      source?: string;
    }
  | {
      op: 'html';
      legacyOp: 'html';
      target: LegacyUiTarget;
      value: string;
      source?: string;
    }
  | {
      op: 'value';
      legacyOp: 'val';
      target: LegacyUiTarget;
      value: string;
      source?: string;
    };

export interface HumanLegacyCommand {
  /**
   * Machine-stable human command name. This is not a treeStore method yet.
   */
  name: string;
  /**
   * Plain-English meaning suitable for review.
   */
  label: string;
  /**
   * Human target category after id/class/tag inference.
   */
  targetKind: LegacyTargetKind;
  /**
   * known: observed/clear meaning.
   * probable: likely meaning, target context still matters.
   * raw: no semantic meaning assigned yet.
   */
  confidence: LegacyCommandConfidence;
  /**
   * Optional future executor command name, once we add treeStore.command.
   */
  futureCommand?: string;
  /**
   * Normalized payload we want to preserve for execution later.
   */
  payload: Record<string, unknown>;
  /**
   * Where this command came from when the normalized op carries source info.
   */
  source?: string;
  /**
   * Notes for reviewers and future implementation.
   */
  notes?: string;
}

interface HumanClassRule {
  className: string;
  addName: string;
  removeName: string;
  toggleName?: string;
  addLabel: string;
  removeLabel: string;
  toggleLabel?: string;
  targetKind?: LegacyTargetKind;
  confidence: LegacyCommandConfidence;
  futureCommand?: string;
  notes?: string;
}

export interface LegacyUiAuditGroup {
  area: string;
  representativeSources: string[];
  normalizedOps: LegacyUiOpName[];
  humanCommands: string[];
  notes: string;
}

export const LEGACY_UI_AUDIT_GROUPS: LegacyUiAuditGroup[] = [
  {
    area: 'sub rows and sub icons',
    representativeSources: [
      'msg/msg.els.js fav icon class swap',
      'msg/msg.els.js hover/qr/sub-link class toggles',
      'msg/msg.checker.js checked + opacity updates',
      'msg/msg.data.js display_none removal',
    ],
    normalizedOps: ['addClass', 'removeClass', 'toggleClass', 'prop', 'fadeTo', 'show', 'hide'],
    humanCommands: [
      'markSubFavoriteOn',
      'markSubFavoriteOff',
      'markQuickReplyIconOn',
      'highlightSubIcon',
      'markSubFlagBlue',
      'dimSub',
      'showElement',
      'hideElement',
    ],
    notes: 'Best first domain for semantic conversion because DataTreeStore already has SubUiState-like fields.',
  },
  {
    area: 'paging',
    representativeSources: [
      'msg/msg.paging.js current page class',
      'msg/msg.paging.js disabled page class',
      'msg/msg.paging.js count label update',
      'msg/msg.paging.js fold labels',
    ],
    normalizedOps: ['toggleClass', 'text', 'show', 'hide', 'toggle'],
    humanCommands: [
      'markPagingButtonCurrent',
      'markPagingButtonDisabled',
      'setCounterText',
      'setLabelText',
      'showElement',
      'hideElement',
    ],
    notes: 'Do not change paging decisions. Only name final UI effects after legacy decides them.',
  },
  {
    area: 'new editor checker',
    representativeSources: [
      'new/checker.js active/check/ok button state',
      'new/checker.js wrong/error classes',
      'new/checker.js disabled prop toggles',
      'new/checker.js generating disabled/value cycle',
    ],
    normalizedOps: ['toggleClass', 'prop', 'attr', 'val', 'text'],
    humanCommands: [
      'markValidationChecking',
      'markValidationOk',
      'markValidationWrong',
      'disableInput',
      'enableInput',
      'setInputValue',
      'setStatusText',
    ],
    notes: 'High value, but fragile. Keep human naming separate from execution until checker smokes cover failure paths.',
  },
  {
    area: 'new editor form values',
    representativeSources: [
      'new/els/body.js body field value/label sync',
      'new/els/body_textarea.js textarea mirror values',
      'new/els/more_settings.js settings values/checked/disabled',
      'new/new.js select disabled/value pairs',
    ],
    normalizedOps: ['val', 'attr', 'prop', 'text', 'show', 'hide', 'toggle'],
    humanCommands: [
      'setInputValue',
      'checkInput',
      'uncheckInput',
      'disableInput',
      'enableInput',
      'showElement',
      'hideElement',
    ],
    notes: 'Long-term source should be msg_inputs through msg_obj facade.',
  },
  {
    area: 'recipients',
    representativeSources: [
      'new/els/to_address.js recipient type classes',
      'new/els/to_address.js recipient label/onclick/to_msg_id',
      'new/els/to_address.js recipient open/closed value + disabled state',
    ],
    normalizedOps: ['addClass', 'attr', 'text', 'val', 'show', 'hide', 'toggle'],
    humanCommands: [
      'markRecipientSelf',
      'markRecipientEmail',
      'markRecipientLocked',
      'setRecipientId',
      'setRecipientLabel',
      'setInputValue',
    ],
    notes: 'Likely becomes a recipient model over msg_inputs, not raw DOM.',
  },
  {
    area: 'attachments and avatar gallery',
    representativeSources: [
      'new/saver.atts.js manual display style update',
      'new/saver.atts.js upload progress text',
      'new/saver.atts.js checked avatar setting',
      'new/saver.atts.js image attrs/src/onclick/file metadata',
      'new/saver.atts.js gallery class/toggle/slideToggle',
    ],
    normalizedOps: ['attr', 'text', 'addClass', 'show', 'hide', 'toggle', 'slideToggle'],
    humanCommands: [
      'setUploadProgressText',
      'markAvatarSelected',
      'setMediaSource',
      'setAttachmentId',
      'showElement',
      'hideElement',
      'toggleElementVisibility',
    ],
    notes: 'Keep raw until upload/avatar flows have stronger tests.',
  },
  {
    area: 'main chrome and menus',
    representativeSources: [
      'msg/msg.els.js menu selected item',
      'msg/msg.els.js foto/body toggle classes',
      'msg/msg.els.js menu/left-menu mode classes',
      'css/css_obj_mcss_mcss.js body mobile/mode classes',
    ],
    normalizedOps: ['addClass', 'removeClass', 'toggleClass', 'text', 'css', 'show', 'hide', 'toggle'],
    humanCommands: [
      'markMenuItemSelected',
      'setPageModeClass',
      'setMobileLayout',
      'markEditMode',
      'setLabelText',
      'showElement',
      'hideElement',
    ],
    notes: 'Template should eventually decide CSS from page/editor state.',
  },
  {
    area: 'alerts and gallery',
    representativeSources: [
      'alert.js alert visibility/buttons/info/input/captcha',
      'msg/subs/glr.std.js popup/grid/back visibility',
      'msg/msg.glr.js gallery answer/submsg counters',
    ],
    normalizedOps: ['toggle', 'text', 'html', 'val', 'attr', 'toggleClass', 'css'],
    humanCommands: [
      'showDialog',
      'hideDialog',
      'setDialogText',
      'setDialogHtml',
      'setInputValue',
      'showGalleryPopup',
      'hideGalleryPopup',
      'setCounterText',
    ],
    notes: 'Human commands are useful now; semantic execution can wait.',
  },
];

export const HUMAN_CLASS_RULES: HumanClassRule[] = [
  {
    className: 'mcss-display_none-cssm',
    addName: 'hideElementByHiddenClass',
    removeName: 'showElementByHiddenClass',
    toggleName: 'toggleElementHiddenClass',
    addLabel: 'hide element using legacy hidden class',
    removeLabel: 'show element by removing legacy hidden class',
    toggleLabel: 'toggle element hidden state using legacy hidden class',
    confidence: 'known',
    futureCommand: 'setVisible',
  },
  {
    className: 'mcss-ic_fav-cssm',
    addName: 'markSubFavoriteOn',
    removeName: 'removeSubFavoriteOnClass',
    addLabel: 'mark sub favorite icon as on',
    removeLabel: 'remove favorite-on icon class',
    targetKind: 'favorite icon',
    confidence: 'known',
    futureCommand: 'setSubFavorite',
  },
  {
    className: 'mcss-ic_fav_grey-cssm',
    addName: 'markSubFavoriteOff',
    removeName: 'removeSubFavoriteOffClass',
    addLabel: 'mark sub favorite icon as off',
    removeLabel: 'remove favorite-off icon class',
    targetKind: 'favorite icon',
    confidence: 'known',
    futureCommand: 'setSubFavorite',
  },
  {
    className: 'mcss-ic_off-cssm',
    addName: 'markFavoriteIconsDisabled',
    removeName: 'markFavoriteIconsEnabled',
    addLabel: 'mark favorite icons as disabled/off',
    removeLabel: 'mark favorite icons as enabled',
    targetKind: 'favorite icon',
    confidence: 'known',
    futureCommand: 'setSubFavoriteEnabled',
  },
  {
    className: 'mcss-ic_qr_on-cssm',
    addName: 'markQuickReplyIconOn',
    removeName: 'markQuickReplyIconOff',
    addLabel: 'mark quick reply icon as on',
    removeLabel: 'mark quick reply icon as off',
    targetKind: 'quick reply icon',
    confidence: 'known',
    futureCommand: 'setQuickReplyHover',
  },
  {
    className: 'mcss-sub_link_on-cssm',
    addName: 'markSubLinkOn',
    removeName: 'markSubLinkOff',
    addLabel: 'mark sub link as on',
    removeLabel: 'mark sub link as off',
    targetKind: 'sub link',
    confidence: 'known',
    futureCommand: 'setSubLinkHover',
  },
  {
    className: 'mcss-ic_ht_red-cssm',
    addName: 'highlightSubIcon',
    removeName: 'unhighlightSubIcon',
    addLabel: 'highlight sub icon on hover',
    removeLabel: 'remove sub icon hover highlight',
    targetKind: 'sub icon',
    confidence: 'known',
    futureCommand: 'setSubHoverHighlighted',
  },
  {
    className: 'mcss-ic_flag_small_blue-cssm',
    addName: 'markSubFlagBlue',
    removeName: 'removeSubFlagBlueClass',
    addLabel: 'mark sub flag as blue/checked',
    removeLabel: 'remove blue flag class',
    targetKind: 'sub icon',
    confidence: 'known',
    futureCommand: 'setSubFlagBlue',
  },
  {
    className: 'mcss-ic_flag_small-cssm',
    addName: 'markSubFlagDefault',
    removeName: 'removeSubFlagDefaultClass',
    addLabel: 'mark sub flag as default/unchecked',
    removeLabel: 'remove default flag class',
    targetKind: 'sub icon',
    confidence: 'known',
    futureCommand: 'setSubFlagBlue',
  },
  {
    className: 'mcss-current_page-cssm',
    addName: 'markPagingButtonCurrent',
    removeName: 'markPagingButtonNotCurrent',
    addLabel: 'mark paging button as current',
    removeLabel: 'mark paging button as not current',
    targetKind: 'paging button',
    confidence: 'known',
    futureCommand: 'setCurrentPageButton',
  },
  {
    className: 'mcss-menu_selected-cssm',
    addName: 'markMenuItemSelected',
    removeName: 'markMenuItemUnselected',
    addLabel: 'mark menu item as selected',
    removeLabel: 'mark menu item as unselected',
    targetKind: 'menu item',
    confidence: 'known',
    futureCommand: 'setSelectedMenuItem',
  },
  {
    className: 'mcss-selected-cssm',
    addName: 'markItemSelected',
    removeName: 'markItemUnselected',
    toggleName: 'toggleItemSelected',
    addLabel: 'mark item as selected',
    removeLabel: 'mark item as unselected',
    toggleLabel: 'toggle item selected state',
    confidence: 'probable',
    futureCommand: 'setSelected',
    notes: 'Used in editor tabs/bars; target context decides exact model.',
  },
  {
    className: 'mcss-active-cssm',
    addName: 'markItemActive',
    removeName: 'markItemInactive',
    toggleName: 'toggleItemActive',
    addLabel: 'mark item as active',
    removeLabel: 'mark item as inactive',
    toggleLabel: 'toggle item active state',
    confidence: 'probable',
    futureCommand: 'setActive',
  },
  {
    className: 'active',
    addName: 'markItemActive',
    removeName: 'markItemInactive',
    toggleName: 'toggleItemActive',
    addLabel: 'mark item as active',
    removeLabel: 'mark item as inactive',
    toggleLabel: 'toggle item active state',
    confidence: 'probable',
    futureCommand: 'setActive',
  },
  {
    className: 'mcss-str_disable-cssm',
    addName: 'markRowVisuallyDisabled',
    removeName: 'markRowVisuallyEnabled',
    addLabel: 'mark row as visually disabled',
    removeLabel: 'mark row as visually enabled',
    confidence: 'known',
    futureCommand: 'setDisabledVisual',
  },
  {
    className: 'mcss-name_key_check_disable-cssm',
    addName: 'markNameKeyCheckDisabled',
    removeName: 'markNameKeyCheckEnabled',
    addLabel: 'mark name-key check control as disabled',
    removeLabel: 'mark name-key check control as enabled',
    targetKind: 'new editor status',
    confidence: 'known',
    futureCommand: 'setCheckerDisabled',
  },
  {
    className: 'mcss-name_key_wrong-cssm',
    addName: 'markValidationWrong',
    removeName: 'clearValidationWrong',
    addLabel: 'mark validation as wrong',
    removeLabel: 'clear validation wrong state',
    targetKind: 'new editor status',
    confidence: 'known',
    futureCommand: 'setValidationState',
  },
  {
    className: 'mcss-info_el_error-cssm',
    addName: 'markInfoTextAsError',
    removeName: 'clearInfoTextError',
    addLabel: 'mark info text as error',
    removeLabel: 'clear info text error state',
    targetKind: 'new editor status',
    confidence: 'known',
    futureCommand: 'setValidationState',
  },
  {
    className: 'mcss-name_key_ok-cssm',
    addName: 'markValidationOk',
    removeName: 'clearValidationOk',
    addLabel: 'mark validation as ok',
    removeLabel: 'clear validation ok state',
    targetKind: 'new editor status',
    confidence: 'known',
    futureCommand: 'setValidationState',
  },
  {
    className: 'mcss-name_key_busy-cssm',
    addName: 'markValidationBusy',
    removeName: 'clearValidationBusy',
    addLabel: 'mark validation as busy/checking',
    removeLabel: 'clear validation busy/checking state',
    targetKind: 'new editor status',
    confidence: 'known',
    futureCommand: 'setValidationState',
  },
  {
    className: 'mcss-new_to_msg_self-cssm',
    addName: 'markRecipientSelf',
    removeName: 'clearRecipientSelf',
    addLabel: 'mark recipient as self/current user',
    removeLabel: 'clear recipient self marker',
    targetKind: 'recipient chip',
    confidence: 'known',
    futureCommand: 'setRecipientKind',
  },
  {
    className: 'mcss-new_to_msg_email-cssm',
    addName: 'markRecipientEmail',
    removeName: 'clearRecipientEmail',
    addLabel: 'mark recipient as email recipient',
    removeLabel: 'clear recipient email marker',
    targetKind: 'recipient chip',
    confidence: 'known',
    futureCommand: 'setRecipientKind',
  },
  {
    className: 'mcss-new_to_msg_non_editable-cssm',
    addName: 'markRecipientLocked',
    removeName: 'markRecipientEditable',
    addLabel: 'mark recipient as non-editable',
    removeLabel: 'mark recipient as editable',
    targetKind: 'recipient chip',
    confidence: 'known',
    futureCommand: 'setRecipientEditable',
  },
];

function normalizeHtmlId(htmlId?: string | null): string | null {
  if (!htmlId) return null;
  const value = String(htmlId).trim();
  return value.startsWith('#') ? value.slice(1) : value;
}

function classesOf(target: LegacyUiTarget): Set<string> {
  return new Set(target.classes || []);
}

export function inferLegacyTargetKind(target: LegacyUiTarget): LegacyTargetKind {
  if (target.targetKind) return target.targetKind;

  const id = normalizeHtmlId(target.htmlId) || '';
  const classes = classesOf(target);
  const tagName = (target.tagName || '').toLowerCase();
  const type = (target.type || target.attrs?.type || '').toLowerCase();

  if (target.attrs?.__MSG_LEGACY_DOCUMENT__ === 'true') return 'document';
  if (target.attrs?.__MSG_LEGACY_WINDOW__ === 'true') return 'window';
  if (id === 'mcss-init_data-cssm' || id === 'mcss-msges_data-cssm' || id === 'mcss-msges_data_json-cssm') return 'legacy data container';
  if (/^mcss-li_id-cssm_\d+$/.test(id)) return 'sub root';
  if (/^mcss-msg_icon-cssm_\d+$/.test(id)) return 'sub icon';
  if (/^mcss-fav-cssm_\d+$/.test(id)) return 'favorite icon';
  if (/^mcss-qr-cssm_\d+$/.test(id)) return 'quick reply icon';
  if (/^mcss-list_txt-cssm_\d+$/.test(id) || classes.has('sub_text') || classes.has('mcss-list_txt_compact-cssm') || classes.has('mcss-sub_text-cssm')) return 'sub text';
  if (/^time_\d+$/.test(id) || classes.has('mcss-list_compact_time-cssm')) return 'sub time';
  if (/^mcss-ul_id-cssm_\d+/.test(id)) return 'subs list';
  if (/^mcss-sub_info/.test(id)) return 'sub info';
  if (id.indexOf('pages') >= 0 || classes.has('mcss-current_page-cssm')) return 'paging button';
  if (id.indexOf('menu') >= 0 || classes.has('mcss-menu_selected-cssm')) return 'menu item';
  if (id === 'mcss-bodyID-cssm' || tagName === 'body') return 'document body';
  if (id.indexOf('new') >= 0 || classes.has('mcss-newmsg-cssm')) return 'new editor form';
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || type === 'checkbox') return 'new editor input';
  if (classes.has('mcss-new_to_msg_list-cssm')) return 'recipient chip';
  if (classes.has('mcss-att_glr-cssm') || classes.has('mcss-avatar_glr-cssm')) return 'attachment gallery';
  if (classes.has('mcss-alert_box_div-cssm')) return 'alert dialog';
  if (classes.has('mcss-gallery-cssm') || classes.has('mcss-img_holder-cssm')) return 'gallery';
  return 'unknown target';
}

function command(
  op: LegacyUiNormalizedOp,
  name: string,
  label: string,
  confidence: LegacyCommandConfidence,
  payload: Record<string, unknown>,
  futureCommand?: string,
  notes?: string
): HumanLegacyCommand {
  return {
    name,
    label,
    targetKind: inferLegacyTargetKind(op.target),
    confidence,
    futureCommand,
    payload,
    source: op.source,
    notes,
  };
}

function rawCommand(op: LegacyUiNormalizedOp, name: string, label: string, payload: Record<string, unknown>): HumanLegacyCommand {
  return command(op, name, label, 'raw', payload, undefined, 'No human semantic meaning is assigned yet; preserve as raw operation.');
}

function boolWord(value: boolean | undefined): 'show' | 'hide' | 'toggle' {
  if (value === true) return 'show';
  if (value === false) return 'hide';
  return 'toggle';
}

function classAction(op: Extract<LegacyUiNormalizedOp, { op: 'class' }>, rule: HumanClassRule): { name: string; label: string } {
  if (op.mode === 'toggle' && op.state === undefined && rule.toggleName && rule.toggleLabel) {
    return { name: rule.toggleName, label: rule.toggleLabel };
  }
  const isAdd = op.mode === 'add' || (op.mode === 'toggle' && op.state === true);
  return isAdd
    ? { name: rule.addName, label: rule.addLabel }
    : { name: rule.removeName, label: rule.removeLabel };
}

function resolveClass(op: Extract<LegacyUiNormalizedOp, { op: 'class' }>): HumanLegacyCommand {
  const rule = HUMAN_CLASS_RULES.find(item => item.className === op.className);
  if (!rule) {
    return rawCommand(op, 'applyRawClass', `apply raw class "${op.className}"`, {
      className: op.className,
      mode: op.mode,
      state: op.state,
    });
  }

  const action = classAction(op, rule);
  return command(
    op,
    action.name,
    action.label,
    rule.confidence,
    {
      className: op.className,
      mode: op.mode,
      state: op.state,
    },
    rule.futureCommand,
    rule.notes,
  );
}

function resolveVisibility(op: Extract<LegacyUiNormalizedOp, { op: 'visibility' }>): HumanLegacyCommand {
  const action = boolWord(op.value);
  const name = action === 'show'
    ? 'showElement'
    : action === 'hide'
      ? 'hideElement'
      : 'toggleElementVisibility';

  return command(op, name, `${action} element`, 'known', {
    value: op.value,
    legacyOp: op.legacyOp,
  }, 'setVisible');
}

function resolveOpacity(op: Extract<LegacyUiNormalizedOp, { op: 'opacity' }>): HumanLegacyCommand {
  const name = op.opacity < 1 ? 'dimElement' : 'undimElement';
  const label = op.opacity < 1 ? `dim element to opacity ${op.opacity}` : `restore element opacity to ${op.opacity}`;
  return command(op, name, label, 'known', { opacity: op.opacity }, 'setOpacity');
}

function boolValue(value: unknown): boolean {
  return value === true || value === 'true' || value === 'checked' || value === 'disabled' || value === 1 || value === '1';
}

function resolveAttr(op: Extract<LegacyUiNormalizedOp, { op: 'attr' }>): HumanLegacyCommand {
  const attrName = op.name.toLowerCase();
  const value = op.value;
  const remove = op.remove === true;

  if (attrName === 'checked') {
    const checked = !remove && boolValue(value);
    return command(op, checked ? 'checkInput' : 'uncheckInput', checked ? 'check input' : 'uncheck input', 'known', {
      name: op.name,
      value,
      remove,
    }, 'setChecked');
  }

  if (attrName === 'disabled') {
    const disabled = !remove && boolValue(value);
    return command(op, disabled ? 'disableInput' : 'enableInput', disabled ? 'disable input' : 'enable input', 'known', {
      name: op.name,
      value,
      remove,
    }, 'setDisabled');
  }

  if (attrName === 'src') {
    return command(op, 'setMediaSource', 'set media source URL', 'probable', { name: op.name, value }, 'setMediaSource');
  }

  if (attrName === 'href') {
    return command(op, 'setLinkHref', 'set link href', 'probable', { name: op.name, value }, 'setLinkHref');
  }

  if (attrName === 'onclick') {
    return command(op, 'setLegacyClickIntent', 'set legacy click intent', 'probable', { name: op.name, value }, 'setEventIntent',
      'Eventually this should become an EventBus intent, not an inline string.');
  }

  if (attrName === 'to_msg_id') {
    return command(op, 'setRecipientId', 'set recipient id', 'known', { name: op.name, value }, 'setRecipientId');
  }

  if (attrName === 'att_id' || attrName === 'file_id' || attrName === 'img_id') {
    return command(op, 'setAttachmentId', 'set attachment/media id', 'known', { name: op.name, value }, 'setAttachmentId');
  }

  return rawCommand(op, 'applyRawAttribute', `apply raw attribute "${op.name}"`, {
    name: op.name,
    value,
    remove,
  });
}

function resolveProp(op: Extract<LegacyUiNormalizedOp, { op: 'prop' }>): HumanLegacyCommand {
  const propName = op.name.toLowerCase();

  if (propName === 'checked') {
    const checked = boolValue(op.value);
    return command(op, checked ? 'checkInput' : 'uncheckInput', checked ? 'check input' : 'uncheck input', 'known', {
      name: op.name,
      value: op.value,
    }, 'setChecked');
  }

  if (propName === 'disabled') {
    const disabled = boolValue(op.value);
    return command(op, disabled ? 'disableInput' : 'enableInput', disabled ? 'disable input' : 'enable input', 'known', {
      name: op.name,
      value: op.value,
    }, 'setDisabled');
  }

  if (propName === 'selected') {
    const selected = boolValue(op.value);
    return command(op, selected ? 'selectOption' : 'unselectOption', selected ? 'select option' : 'unselect option', 'known', {
      name: op.name,
      value: op.value,
    }, 'setSelected');
  }

  return rawCommand(op, 'applyRawProperty', `apply raw property "${op.name}"`, {
    name: op.name,
    value: op.value,
  });
}

function resolveStyle(op: Extract<LegacyUiNormalizedOp, { op: 'style' }>): HumanLegacyCommand {
  const prop = op.prop.trim().toLowerCase();
  const value = op.value;

  if (prop === 'display') {
    const visible = value !== 'none';
    return command(op, visible ? 'showElementByDisplayStyle' : 'hideElementByDisplayStyle', visible ? 'show element by display style' : 'hide element by display style', 'known', {
      prop,
      value,
    }, 'setVisible');
  }

  if (prop === 'opacity') {
    const opacity = typeof value === 'number' ? value : Number.parseFloat(String(value ?? '1'));
    return command(op, opacity < 1 ? 'dimElement' : 'undimElement', opacity < 1 ? `dim element to opacity ${opacity}` : `restore element opacity to ${opacity}`, 'known', {
      prop,
      value,
      opacity,
    }, 'setOpacity');
  }

  if (prop === 'visibility') {
    return command(op, 'setElementVisibilityStyle', 'set CSS visibility style', 'probable', { prop, value }, 'setVisibilityStyle');
  }

  if (prop === 'color' || prop === 'font-weight' || prop === 'background' || prop === 'text-decoration') {
    return command(op, 'setVisualStyle', `set visual style "${prop}"`, 'probable', { prop, value }, 'setVisualStyle',
      'Often button/status presentation. Template should eventually decide this from semantic state.');
  }

  return rawCommand(op, 'applyRawStyle', `apply raw style "${op.prop}"`, { prop: op.prop, value });
}

function resolveText(op: Extract<LegacyUiNormalizedOp, { op: 'text' }>): HumanLegacyCommand {
  const kind = inferLegacyTargetKind(op.target);
  const id = normalizeHtmlId(op.target.htmlId) || '';

  if (kind === 'legacy data container') {
    return command(op, 'setLegacyDataPayloadText', 'set legacy data payload text', 'known', { value: op.value }, 'setDataPayload');
  }

  if (id.indexOf('count') >= 0 || id.indexOf('counter') >= 0 || /^\([0-9+]+\)$/.test(op.value)) {
    return command(op, 'setCounterText', 'set counter text', 'probable', { value: op.value }, 'setCounterText');
  }

  if (kind === 'new editor status') {
    return command(op, 'setStatusText', 'set status text', 'probable', { value: op.value }, 'setStatusText');
  }

  if (kind === 'sub text') {
    return command(op, 'setSubText', 'set sub text', 'known', { value: op.value }, 'setSubText');
  }

  if (kind === 'sub time') {
    return command(op, 'setSubTimeText', 'set sub time text', 'known', { value: op.value }, 'setSubTimeText');
  }

  return command(op, 'setLabelText', 'set label text', 'probable', { value: op.value }, 'setTextContent');
}

function resolveHtml(op: Extract<LegacyUiNormalizedOp, { op: 'html' }>): HumanLegacyCommand {
  const value = op.value;
  const kind = inferLegacyTargetKind(op.target);

  if (value.indexOf('mcss-li_id-cssm_') >= 0 || value.indexOf('mcss-msges_data') >= 0) {
    return command(op, 'setSubsHtmlPayload', 'set sub messages HTML payload', 'known', { value }, 'setSubsPayload');
  }

  if (kind === 'new editor form' || value.indexOf('mcss-newmsg-cssm') >= 0) {
    return command(op, 'setNewEditorHtml', 'set new editor HTML', 'known', { value }, 'setNewEditorHtml');
  }

  if (kind === 'main message body') {
    return command(op, 'setMainMessageHtml', 'set main message HTML', 'probable', { value }, 'setMessageHtml');
  }

  if (kind === 'sub text') {
    return command(op, 'setSubHtml', 'set sub HTML', 'known', { value }, 'setSubHtml');
  }

  return rawCommand(op, 'setRawHtml', 'set raw HTML', { value });
}

function resolveValue(op: Extract<LegacyUiNormalizedOp, { op: 'value' }>): HumanLegacyCommand {
  return command(op, 'setInputValue', 'set input value', 'known', { value: op.value }, 'setInputValue');
}

export function resolveLegacyUiCommand(op: LegacyUiNormalizedOp): HumanLegacyCommand {
  switch (op.op) {
    case 'class':
      return resolveClass(op);
    case 'visibility':
      return resolveVisibility(op);
    case 'opacity':
      return resolveOpacity(op);
    case 'attr':
      return resolveAttr(op);
    case 'prop':
      return resolveProp(op);
    case 'style':
      return resolveStyle(op);
    case 'text':
      return resolveText(op);
    case 'html':
      return resolveHtml(op);
    case 'value':
      return resolveValue(op);
  }
}

export const legacyUiCommandRouter = {
  auditGroups: LEGACY_UI_AUDIT_GROUPS,
  classRules: HUMAN_CLASS_RULES,
  inferTargetKind: inferLegacyTargetKind,
  resolve: resolveLegacyUiCommand,
};
